import type React from 'react';
import KeepAlive from 'react-activation';
import { useTranslation } from 'react-i18next';
import { Grid, type GridProps, Stack, Typography, useMediaQuery } from '@mui/material';
import { styled } from '@mui/material/styles';
import type { Flow } from '@/constants/flow';
import { useCustom } from '@/hooks/use-custom';
import { useBannerStore } from '@/store/use-banner-store';
import { Logo } from '../components/logo';

const BannerGrid = styled(Grid)<
  GridProps & {
    centeredDirection?: 'row' | 'row-reverse';
    isAr?: boolean;
  }
>(({ theme, centeredDirection }) => ({
  borderRadius:
    centeredDirection === 'row-reverse'
      ? [0, theme.spacing(1), theme.spacing(1), 0].join(' ')
      : [theme.spacing(1), 0, 0, theme.spacing(1)].join(' '),
  overflow: 'hidden',
  [theme.breakpoints.down('sm')]: {
    marginTop: theme.spacing(-3),
    marginBottom: theme.spacing(-3),
    marginLeft: centeredDirection === 'row-reverse' ? 0 : theme.spacing(-3),
    width: `calc(100% + ${theme.spacing(3)})`,
  },
}));

const BannerText = () => {
  const { t } = useTranslation();
  const { componentConfig } = useCustom();
  const { titleKey, descKey } = componentConfig?.banner || {};

  return (
    <Grid size={12}>
      <Stack direction="column" spacing={3}>
        <Typography
          variant="pageTitle"
          component="h1"
          sx={{
            fontWeight: 600,
            wordBreak: 'break-word',
          }}
        >
          {t(titleKey || 'brand_title')}
        </Typography>
        <Typography>{t(descKey || 'brand_description')}</Typography>
      </Stack>
    </Grid>
  );
};

const ImageGallery: React.FC = () => {
  const { componentConfig } = useCustom();
  const { isImageLoaded, setBanner } = useBannerStore();

  const imageNames = componentConfig?.banner?.images || [];
  const images = imageNames.map((name) => new URL(`../../../assets/banner/${name}`, import.meta.url).href);
  const img = images[0];

  return (
    <Stack sx={{ visibility: isImageLoaded ? 'visible' : 'hidden' }}>
      <img
        alt="banner cover"
        src={img}
        className="h-full w-full"
        onError={() => {
          setBanner({ isImageError: true });
        }}
        onLoad={() => {
          setBanner({ isImageLoaded: true });
        }}
      />
    </Stack>
  );
};

export const Banner: React.FC<{ backFlow?: Flow }> = ({ backFlow }) => {
  const { i18n } = useTranslation();
  const { componentConfig, layoutConfig } = useCustom();
  const { images } = componentConfig?.banner || {};
  const isAr = i18n.language.toLowerCase() === 'ar';
  const showLogo = layoutConfig?.layout !== 'space-between';
  const isUpLarge = useMediaQuery((theme) => theme.breakpoints.up('lg'));
  const showImageGallery = images && images.length > 0;
  const showBlankLogo = backFlow || (showLogo && isUpLarge) || (showImageGallery && !showLogo && isUpLarge);

  const direction = layoutConfig?.centeredDirection || 'row';
  const bannerGridDirection: 'row' | 'row-reverse' = isAr ? (direction === 'row' ? 'row-reverse' : 'row') : direction;

  const { isImageError } = useBannerStore();

  const logoSectionDisplay = !showImageGallery || isImageError || !isUpLarge;
  const bannerTextDisplay = isUpLarge && (!showImageGallery || isImageError);
  const bannerImageGalleryDisplay = showImageGallery && isUpLarge && !isImageError;

  const cacheKey = 'BannerImageGallery';

  return (
    <Grid
      direction="column"
      container
      size={{
        xs: 12,
        lg: isUpLarge ? 6 : 12,
      }}
      sx={(theme: any) => ({
        padding: { sm: bannerImageGalleryDisplay ? 0 : theme.spacing(6), xs: theme.spacing(3) },
        paddingBottom: {
          lg: bannerImageGalleryDisplay ? 0 : theme.spacing(6),
          md: 0,
          sm: 0,
          xs: 0,
        },
      })}
    >
      {logoSectionDisplay && (
        <Grid
          size={12}
          sx={(theme) => ({
            ...(showBlankLogo && theme.mixins.headerPlaceholder),
            mb: showBlankLogo ? 3 : 0,
          })}
        >
          {showLogo && <Logo />}
        </Grid>
      )}

      {bannerTextDisplay && <BannerText />}
      {bannerImageGalleryDisplay && (
        <BannerGrid
          size={12}
          centeredDirection={bannerGridDirection}
          isAr={isAr}
          sx={{ height: '100%', overflow: 'hidden' }}
        >
          <KeepAlive name={cacheKey} cacheKey={cacheKey}>
            <ImageGallery />
          </KeepAlive>
        </BannerGrid>
      )}
    </Grid>
  );
};
